'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import type { AuthError } from 'firebase/auth';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { RHFShadcnTextField } from '@/components/RHF';
import FormProvider from '@/components/RHF/FormProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormValidationSummary } from '@/components/ui/form-validation-summary';
import { Icon } from '@/components/ui/icon';
import { toast } from '@/components/ui/use-toast';
import { frontEndRoutes } from '@/constants/routes';
import { AuthLayout } from '@/containers/layouts/AuthLayout';
import { AuthTextLink } from '@/containers/screens/page-auth-shared/AuthTextLink';
import { getFirebaseAuthErrorMessage } from '@/containers/screens/page-auth-shared/getFirebaseAuthErrorMessage';
import {
  handleAuthFailureWithLinking,
  signInWithGoogle,
  signInWithMicrosoft,
  signInWithPassword,
} from '@/services/firebaseServices/AuthServices/authentication';
import type { EnvironmentDisplayInfo } from '@/services/helpers';
import {
  getEnvironmentDisplayInfo,
} from '@/services/helpers';
import type { SignInWithPasswordFormFields } from '@/types/auth';
import { extractSpaceFromClaims, getClaimsFromJWTCookie, getFirstAccessibleRouteClient } from '@/utils/auth-helpers';
import { getTargetEnvironmentClient } from '@/utils/target-detection';
import { createSignInWithPasswordSchema } from '@/validations/auth';

/**
 * Resolves the redirect route after successful authentication.
 * Extracts space from JWT claims and routes to the first accessible page.
 */
function resolvePostAuthRedirect(): string {
  const claims = getClaimsFromJWTCookie();
  if (claims === null) {
    return frontEndRoutes.dashboard.base;
  }

  const spaceId = extractSpaceFromClaims(claims);
  if (spaceId !== null && spaceId !== '') {
    return getFirstAccessibleRouteClient(claims, spaceId);
  }

  return frontEndRoutes.dashboard.base;
}

/**
 * Maps Firebase authentication error codes to the sign-in translation keys.
 * Codes not listed here fall back to `auth.errors.genericError`.
 */
const SIGN_IN_ERROR_KEYS: Record<string, string> = {
  'auth/invalid-credential': 'auth.errors.invalidCredentials',
  'auth/wrong-password': 'auth.errors.invalidCredentials',
  'auth/user-not-found': 'auth.errors.invalidCredentials',
  'auth/invalid-email': 'auth.errors.invalidEmail',
  'auth/user-disabled': 'auth.errors.userDisabled',
  'auth/too-many-requests': 'auth.errors.tooManyRequests',
  'auth/network-request-failed': 'auth.errors.networkError',
  'auth/popup-closed-by-user': 'auth.errors.popupClosed',
  'auth/cancelled-popup-request': 'auth.errors.popupCancelled',
  'auth/account-exists-with-different-credential': 'auth.errors.accountExistsWithDifferentCredential',
};

function getAuthErrorMessage(errorCode: string, translate: (key: string) => string): string {
  return getFirebaseAuthErrorMessage(errorCode, translate, {
    codeToKey: SIGN_IN_ERROR_KEYS,
    defaultKey: 'auth.errors.genericError',
  });
}

/**
 * Maps custom error messages (non-Firebase) to user-friendly messages
 */
function getCustomErrorMessage(errorMessage: string, translate: (key: string) => string): string {
  if (errorMessage === 'EMAIL_PASSWORD_SIGN_IN_REQUIRED') {
    return translate('auth.errors.emailPasswordSignInRequired');
  }
  return translate('auth.errors.genericError');
}

function resolveEnvironmentBadge(): { environment: EnvironmentDisplayInfo | null; showEnvBadge: boolean } {
  const targetEnv = getTargetEnvironmentClient();
  const envInfo = getEnvironmentDisplayInfo(targetEnv);
  const envName = envInfo.name.toLowerCase();
  const showEnvBadge = envName.includes('staging') === false && envName.includes('production') === false;
  return { environment: envInfo, showEnvBadge };
}

export function SignInPage() {
  const translate = useTranslations();
  const router = useRouter();
  // Resolved client-side after mount: `getTargetEnvironmentClient` reads the
  // current hostname, which is unavailable during SSR. Resolving in a state
  // initializer left the badge permanently empty.
  const [{ environment, showEnvBadge }, setEnvBadge] = useState<{
    environment: EnvironmentDisplayInfo | null;
    showEnvBadge: boolean;
  }>({ environment: null, showEnvBadge: false });

  useEffect(() => {
    setEnvBadge(resolveEnvironmentBadge());
  }, []);

  const signInWithPasswordSchemaInstance = createSignInWithPasswordSchema({
    emailRequired: translate('auth.validation.emailRequired'),
    emailInvalid: translate('auth.validation.emailInvalid'),
    passwordRequired: translate('auth.validation.passwordRequired'),
    passwordMinLength: translate('auth.validation.passwordMinLength'),
  });

  const methods = useForm<SignInWithPasswordFormFields>({
    mode: 'onChange',
    resolver: zodResolver(signInWithPasswordSchemaInstance),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { handleSubmit, formState: { isSubmitting } } = methods;
  const [activeOAuthProvider, setActiveOAuthProvider] = useState<'google' | 'microsoft' | null>(null);
  const isRedirectingRef = useRef(false);
  const isOAuthLoading = activeOAuthProvider !== null;
  const isAuthInProgress = isSubmitting === true || isOAuthLoading === true;

  const onSubmit = async (data: SignInWithPasswordFormFields) => {
    try {
      if (data.password === undefined || data.password === '') {
        throw new Error(translate('auth.validation.passwordRequired'));
      }
      await signInWithPassword(data.email, data.password);

      isRedirectingRef.current = true;
      router.push(resolvePostAuthRedirect());
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code ?? '';
      const httpStatus = (error as { status?: number })?.status;
      const isRateLimited = errorCode === 'auth/too-many-requests' || httpStatus === 429;
      const errorMessage = isRateLimited === true
        ? translate('auth.errors.tooManyRequests')
        : getAuthErrorMessage(errorCode, translate);

      toast({
        title: translate('auth.errors.signInFailed'),
        description: errorMessage,
        variant: 'error' as const,
      });
    }
  };

  const handleGoogleSignInClick = useCallback(async () => {
    setActiveOAuthProvider('google');
    try {
      await signInWithGoogle();

      isRedirectingRef.current = true;
      router.push(resolvePostAuthRedirect());
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code ?? '';
      const errorMessage = getAuthErrorMessage(errorCode, translate);

      toast({
        title: translate('auth.errors.signInFailed'),
        description: errorMessage,
        variant: 'error' as const,
      });

      if (isRedirectingRef.current === false) {
        setActiveOAuthProvider(null);
      }
    }
  }, [router, translate]);

  const handleMicrosoftSignInClick = useCallback(async () => {
    setActiveOAuthProvider('microsoft');
    try {
      await signInWithMicrosoft();

      isRedirectingRef.current = true;
      router.push(resolvePostAuthRedirect());
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code ?? '';

      if (errorCode === 'auth/account-exists-with-different-credential') {
        try {
          const linkedResult = await handleAuthFailureWithLinking(error as AuthError);

          if (linkedResult !== null) {
            toast({
              title: translate('auth.success.accountLinked'),
              description: translate('auth.success.accountLinkedDescription'),
              variant: 'success' as const,
            });

            isRedirectingRef.current = true;
            router.push(resolvePostAuthRedirect());
            return;
          }
        } catch (linkError: unknown) {
          const linkErrorMessage = (linkError as { message?: string })?.message ?? '';

          if (linkErrorMessage === 'EMAIL_PASSWORD_SIGN_IN_REQUIRED') {
            toast({
              title: translate('auth.errors.accountLinkingFailed'),
              description: getCustomErrorMessage(linkErrorMessage, translate),
              variant: 'error' as const,
            });
            setActiveOAuthProvider(null);
            return;
          }

          const linkErrorCode = (linkError as { code?: string })?.code ?? '';
          const errorMessage = getAuthErrorMessage(linkErrorCode, translate);

          toast({
            title: translate('auth.errors.accountLinkingFailed'),
            description: errorMessage,
            variant: 'error' as const,
          });
          setActiveOAuthProvider(null);
          return;
        }
      }

      const errorMessage = getAuthErrorMessage(errorCode, translate);

      toast({
        title: translate('auth.errors.signInFailed'),
        description: errorMessage,
        variant: 'error' as const,
      });

      if (isRedirectingRef.current === false) {
        setActiveOAuthProvider(null);
      }
    }
  }, [router, translate]);

  return (
    <AuthLayout>
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <h1 className="type-headline-m md:type-headline-l text-foreground">
            {translate('auth.signIn.title')}
          </h1>
          <p className="type-body-l text-on-surface-variant text-balance">
            {translate('auth.signIn.description')}
          </p>
        </header>

        <FormProvider methods={methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <RHFShadcnTextField
              name="email"
              title={translate('auth.signIn.emailLabel')}
              fieldType="email"
              autoComplete="email"
              disabled={isAuthInProgress === true}
            />

            <div className="flex flex-col gap-2">
              <RHFShadcnTextField
                name="password"
                title={translate('auth.signIn.passwordLabel')}
                fieldType="password"
                autoComplete="current-password"
                disabled={isAuthInProgress === true}
              />
              {/* Forgot-password link sits with the field it relates to. */}
              <AuthTextLink
                href={frontEndRoutes.auth.forgotPassword}
                className="type-body-s text-secondary self-end"
              >
                {translate('auth.signIn.forgotPassword')}
              </AuthTextLink>
            </div>

            <FormValidationSummary
              heading={translate('common.form.fixTheFollowing')}
              fieldLabels={{
                email: translate('auth.signIn.emailLabel'),
                password: translate('auth.signIn.passwordLabel'),
              }}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={isSubmitting === true}
              disabled={isAuthInProgress === true}
            >
              {translate('auth.signIn.signInButton')}
            </Button>
          </form>
        </FormProvider>

        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-outline-variant" />
          <span className="type-label-s text-on-surface-variant">
            {translate('auth.signIn.orContinueWith')}
          </span>
          <span className="h-px flex-1 bg-outline-variant" />
        </div>

        <div className="grid grid-cols-1 gap-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleGoogleSignInClick}
            disabled={isAuthInProgress === true}
            loading={activeOAuthProvider === 'google'}
          >
            <Icon icon="logos:google-icon" size={18} className="mr-2 shrink-0" />
            {translate('auth.signIn.continueWithGoogle')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleMicrosoftSignInClick}
            disabled={isAuthInProgress === true}
            loading={activeOAuthProvider === 'microsoft'}
          >
            <Icon icon="logos:microsoft-icon" size={18} className="mr-2 shrink-0" />
            {translate('auth.signIn.continueWithMicrosoft')}
          </Button>
        </div>

        <footer className="flex flex-col items-center gap-3 border-t border-outline-variant pt-6">
          <p className="type-body-s text-center text-on-surface-variant text-balance">
            {translate('auth.signIn.termsText')}
          </p>
          {showEnvBadge === true && environment !== null && (
            <Badge variant="outline" size="sm">
              {environment.name}
            </Badge>
          )}
        </footer>
      </div>
    </AuthLayout>
  );
}
